import { Action } from 'history';
import { useCreateStore } from 'hooks/createStore';
import React, {
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';
import { noop } from 'shared/utils/function';
import {
  getInspectionEditPathFromLocation,
  getIssueNavigationState,
  getPreviousLocationsSnapshot,
  isInspectionDetailsPage,
  isInspectionListPage,
  isEditOrCreateOrTemplatePath,
  isIssueDetailsPath,
  isIssueListReturnUrl,
  isRelevantLocationChange,
  popLastTwoLocations,
  shouldRedirectChecklistToInspectionEdit,
  canReturnToLocation,
  buildUrl,
  resolveTitle,
  handleReplaceLocation,
} from './model';
import { Backtrack, Location } from './types';
import { useAppNavigation } from 'contexts/navigation';
import { mergeLocationState } from 'helpers/navigationState';
import { createStrictOptionalContext } from 'helpers/context';

const { Provider: BacktrackProvider, useRequiredContext: useBacktrack } =
  createStrictOptionalContext<Backtrack>('useBacktrack', 'WithBacktrack');

const WithBacktrack: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const titleStore = useCreateStore<string | undefined>(undefined);
  const locationsRef = useRef<Location[]>([
    { pathname: '/issue', state: undefined },
  ]);
  const { goToIssues } = useAppNavigation();
  const backHandler = useCallback(() => {
    const { returnUrl } = getIssueNavigationState(location.state);

    if (
      isIssueListReturnUrl(returnUrl) &&
      isIssueDetailsPath(location.pathname)
    ) {
      navigate(returnUrl, {
        replace: true,
        state: mergeLocationState(location.state, {
          fromIssueDetails: true,
        }),
      });
      return;
    }

    const { prevLocation, prevPrevLocation } =
      getPreviousLocationsSnapshot(locationsRef.current);
    const wasInEditOrCreate = isEditOrCreateOrTemplatePath(
      prevPrevLocation?.pathname
    );

    popLastTwoLocations(locationsRef.current);
    
    if (!prevLocation) {
      goToIssues();
    } else if (canReturnToLocation(prevLocation)) {
      const shouldMarkFromInspectionDetails =
        isInspectionDetailsPage(location.pathname) &&
        isInspectionListPage(prevLocation.pathname);

      if (
        shouldRedirectChecklistToInspectionEdit(
          prevLocation,
          wasInEditOrCreate
        )
      ) {
        const inspectionEditPath = getInspectionEditPathFromLocation(
          prevLocation.pathname
        );
        if (inspectionEditPath) {
          navigate(inspectionEditPath, {
            state: prevLocation.state,
          });
          return;
        }
      }
      navigate(buildUrl(prevLocation), {
        state: shouldMarkFromInspectionDetails
          ? mergeLocationState(prevLocation.state, {
              fromInspectionDetails: true,
            })
          : prevLocation.state,
      });
    } else {
      backHandler();
    }
  }, [goToIssues, navigate, location.pathname, location.state]);

  const pushLocation = useCallback(
    (location: Location) => {
      if (!isRelevantLocationChange(location, locationsRef.current)) {
        return;
      }
      locationsRef.current.push(location);
      titleStore.set(
        resolveTitle(
          locationsRef.current.slice(-1)[0],
          locationsRef.current
        )
      );
    },
    [titleStore]
  );

  const replaceLocation = useCallback(
    (location: Location) => {
      if (!isRelevantLocationChange(location, locationsRef.current)) {
        return;
      }
      locationsRef.current = handleReplaceLocation(location, locationsRef.current);
      titleStore.set(
        resolveTitle(
          locationsRef.current.slice(-1)[0],
          locationsRef.current
        )
      );
    },
    [titleStore]
  );

  const actionHandlers = useMemo<
    Record<Action, (location: Location) => void>
  >(
    () => ({ POP: noop, PUSH: pushLocation, REPLACE: replaceLocation }),
    [pushLocation, replaceLocation]
  );

  useEffect(() => {
    if (navigationType) {
      actionHandlers[navigationType]({
        ...location,
        state: location.state,
      });
    }
  }, [location, navigationType, actionHandlers]);

  const ctx: Backtrack = useMemo(
    () => ({
      titleStore,
      back: backHandler,
      pushLocation,
      replaceLocation,
    }),
    [backHandler, titleStore, pushLocation, replaceLocation]
  );

  return (
    <BacktrackProvider value={ctx}>
      {children}
    </BacktrackProvider>
  );
};

export const withBacktrack =
  (Component: React.ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithBacktrack>
      <Component {...props} />
    </WithBacktrack>
  );

export { WithBacktrack, useBacktrack };
